import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

import {
  Container,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Box,
  Tabs,
  Tab,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Menu,
  Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SchoolIcon from '@mui/icons-material/School';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';

const cleanFileName = (filePath) => {
  if (!filePath) return '';
  if (filePath.startsWith('http')) {
    return decodeURIComponent(filePath.split('/').pop().replace(/^\d+[-_]/, ''));
  }
  return filePath;
};

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ViewEmployees = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [companyCountry, setCompanyCountry] = useState("India");
  const [nationalIdLabel, setNationalIdLabel] = useState("PAN / Aadhaar");
  const [bankCodeLabel, setBankCodeLabel] = useState("IFSC Code");

  // Initialize from URL params
  const page = parseInt(searchParams.get('page')) || 1;
  const rowsPerPage = parseInt(searchParams.get('limit')) || 10;
  const searchQuery = searchParams.get('search') || '';
  const searchField = searchParams.get('field') || 'all';

  // Local state for search query to avoid constant URL updates
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Sync local search query with URL params
  useEffect(() => {
    setLocalSearchQuery(searchQuery);
  }, [searchQuery]);

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTab, setDialogTab] = useState(0);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Edit Status State
  const [editStatusOpen, setEditStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Edit Cost Center State
  const [editCostCenterOpen, setEditCostCenterOpen] = useState(false);
  const [selectedCostCenterIds, setSelectedCostCenterIds] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [updatingCostCenter, setUpdatingCostCenter] = useState(false);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Update URL params
  const updateSearchParams = (newParams) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    setSearchParams(params, { replace: true });
  };

  // Filter function for client-side search
  const filterEmployees = (employeesList, query, field) => {
    if (!query.trim()) return employeesList;
    const lowerQuery = query.toLowerCase();
    return employeesList.filter(emp => {
      switch (field) {
        case 'all':
          const fullName = `${emp.personal.firstName || ''} ${emp.personal.lastName || ''}`.toLowerCase();
          const email = emp.personal.personalemail?.toLowerCase() || '';
          const id = emp.employee_id?.toLowerCase() || '';
          const dept = (emp.financial.department || emp.roles.department || '').toLowerCase();
          return fullName.includes(lowerQuery) || email.includes(lowerQuery) || id.includes(lowerQuery) || dept.includes(lowerQuery);
        case 'employee_id':
          return emp.employee_id?.toLowerCase().includes(lowerQuery);
        case 'firstName':
          return emp.personal.firstName?.toLowerCase().includes(lowerQuery);
        case 'lastName':
          return emp.personal.lastName?.toLowerCase().includes(lowerQuery);
        case 'department':
          const deptFilter = (emp.financial.department || emp.roles.department || '').toLowerCase();
          return deptFilter.includes(lowerQuery);
        default:
          return true;
      }
    });
  };

  useEffect(() => {
    fetchEmployees();
  }, [retryCount, page, rowsPerPage, searchQuery, searchField]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      if (company_id) {
        try {
          const companyResponse = await axios.get(`${API_URL}/api/cmp/${company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (companyResponse.data.success && companyResponse.data.data?.country) {
            setCompanyCountry(companyResponse.data.data.country);
            if (companyResponse.data.data.nationalIdLabel) setNationalIdLabel(companyResponse.data.data.nationalIdLabel);
            if (companyResponse.data.data.bankCodeLabel) setBankCodeLabel(companyResponse.data.data.bankCodeLabel);
          }
        } catch (cErr) {
          // console.error("Error fetching company country:", cErr);
        }
      }

      // Build query params for pagination and search
      const params = new URLSearchParams({
        company_id,
        page: searchQuery.trim() ? '1' : page.toString(),
        limit: searchQuery.trim() ? '1000' : rowsPerPage.toString()
      });

      // Do not append search params to backend; handle client-side if searching

      // Fetch paginated personal data
      const personalResponse = await axios.get(`${API_URL}/api/viewpersonal?${params.toString()}`,
        { headers: {
          Authorization: `Bearer ${token}`
        }}  
    );

      // For financial and roles, fetch only for the employee_ids in current page (optimized)
      let financialMap = {};
      let rolesMap = {};

      if (personalResponse.data.success && personalResponse.data.data.length > 0) {
        const employeeIds = personalResponse.data.data.map(emp => emp.employee_id).join(',');

        // Fetch financial for specific employee_ids
        const financialResponse = await axios.get(`${API_URL}/api/viewallfinancial?employee_ids=${employeeIds}`,
          { headers: {
            Authorization: `Bearer ${token}`
          }}
        );
        if (financialResponse.data && Array.isArray(financialResponse.data)) {
          financialResponse.data.forEach(emp => {
            financialMap[emp.employee_id] = {
              costCenter: emp.costCenter ? `${emp.costCenter.name} (${emp.costCenter.code})` : "Not Assigned",
              costCenterId: emp.costCenterId,
              costCenterIds: emp.costCenterIds || (emp.costCenterId ? [emp.costCenterId] : []),
              bankName: emp.bankName,
              accountHolderName: emp.accountHolderName,
              accountNumber: emp.accountNumber,
              accountType: emp.accountType,
              branch: emp.branch,
              ifscCode: emp.ifscCode,
              department: emp.department,
              currentSalary: emp.currentSalary,
              previousSalary: emp.previousSalary,
              ctc: emp.ctc,
              taxCalculation: emp.taxCalculation,
              resignationDate: emp.resignationDate,
              noticePeriod: emp.noticePeriod,
              advanceSalary: emp.advanceSalary,
              creditCardOffered: emp.creditCardOffered
            };
          });
        }

        // Fetch roles for specific employee_ids
        const rolesResponse = await axios.get(`${API_URL}/api/roles?employee_ids=${employeeIds}`,{ headers: {
          Authorization: `Bearer ${token}`
        }});
        if (rolesResponse.data.success && Array.isArray(rolesResponse.data.data)) {
          rolesResponse.data.data.forEach(role => {
            rolesMap[role.employee_id] = {
              fullName: role.fullName,
              email: role.email,
              designation: role.designation,
              department: role.department,
              roleType: role.roleType,
              reportingManager: role.reportingManager,
              teamSize: role.teamSize,
              joiningDate: role.joiningDate,
              selectedResponsibilities: role.selectedResponsibilities,
              additionalResponsibilities: role.additionalResponsibilities,
              grade: role.grade
            };
          });
        }
      }


      // Process personal data (already paginated)
      let personalEmployees = [];
      if (personalResponse.data.success && Array.isArray(personalResponse.data.data)) {
        personalEmployees = personalResponse.data.data.map(emp => ({
          employee_id: emp.employee_id,
          personal: {
            // Personal Information
            firstName: emp.firstName,
            lastName: emp.lastName,
            fatherName: emp.fatherName,
            personalemail: emp.personalemail,
            companyemail: emp.companyemail,
            dateOfBirth: emp.dateOfBirth,
            anniversary: emp.anniversary,
            gender: emp.gender,
            phoneNumber: emp.phoneNumber,
            tenure: emp.tenure,  // NEW: Added tenure

            // Address Information
            houseNumber: emp.houseNumber,
            street: emp.street,
            state: emp.state,
            area: emp.area,
            city: emp.city,
            pinCode: emp.pinCode,
            mobile: emp.mobile,
            landline: emp.landline,

            // Documents
            panNumber: emp.panNumber,
            panCardFile: emp.panCardFile,
            adharCardNumber: emp.adharCardNumber,
            adharCardFile: emp.adharCardFile,
            personalPhoto: emp.personalPhoto,

            // Education
            degree: emp.degree,
            institution: emp.institution,
            year: emp.year,
            qualificationFile: emp.qualificationFile,

            // Certifications
            certificationName: emp.certificationName,
            issuedBy: emp.issuedBy,
            certificationDate: emp.certificationDate,
            certificationFile: emp.certificationFile,

            // Nominee & Insurance
            nomineeName: emp.nomineeName,
            relationship: emp.relationship,
            nomineeAge: emp.nomineeAge,
            individualInsurance: emp.individualInsurance,
            groupInsurance: emp.groupInsurance,

            // Employment
            employmentStatus: emp.employmentStatus,

            // Timestamps
            updatedAt: emp.updatedAt,
            createdAt: emp.createdAt
          }
        }));
      }

      // Merge all data
      const mergedEmployees = personalEmployees.map(emp => ({
        ...emp,
        financial: financialMap[emp.employee_id] || {},
        roles: rolesMap[emp.employee_id] || {}
      }));

      // Sort by updated date (server already sorts, but keep for consistency)
      const sortedEmployees = mergedEmployees.sort((a, b) => {
        const aUpdate = a.personal.updatedAt ? new Date(a.personal.updatedAt) : null;
        const bUpdate = b.personal.updatedAt ? new Date(b.personal.updatedAt) : null;
        const aCreate = a.personal.createdAt ? new Date(a.personal.createdAt) : new Date(0);
        const bCreate = b.personal.createdAt ? new Date(b.personal.createdAt) : new Date(0);

        if (aUpdate && bUpdate) return bUpdate - aUpdate;
        if (aUpdate) return -1;
        if (bUpdate) return 1;
        return bCreate - aCreate;
      });

      // Apply client-side filtering if searching
      let finalEmployees = sortedEmployees;
      let finalCount = personalResponse.data.count || 0;
      setIsSearching(searchQuery.trim());
      if (searchQuery.trim()) {
        finalEmployees = filterEmployees(sortedEmployees, searchQuery, searchField);
        finalCount = finalEmployees.length;
      } else {
        setIsSearching(false);
      }

      setEmployees(finalEmployees);
      setTotalCount(finalCount);
    } catch (error) {
      //console.error("Fetch error:", error);
      setError('Failed to load employees');
      setEmployees([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) return;
    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem("token");
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await axios.put(`${API_URL}/api/updateEmployeeStatus/${selectedEmployee.employee_id}`,   {
        employmentStatus: newStatus
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      
      });

      if (response.data.success) {
        // Update local state to reflect changes instantly without refetching everything
        const updatedEmployees = employees.map(emp => {
          if (emp.employee_id === selectedEmployee.employee_id) {
            return {
              ...emp,
              personal: {
                ...emp.personal,
                employmentStatus: newStatus
              }
            };
          }
          return emp;
        });
        setEmployees(updatedEmployees);

        // Update selected employee too so the dialog shows new status
        setSelectedEmployee({
          ...selectedEmployee,
          personal: {
            ...selectedEmployee.personal,
            employmentStatus: newStatus
          }
        });

        setEditStatusOpen(false);
      }
    } catch (error) {
      //console.error("Failed to update status:", error);
      alert(error.response?.data?.message || "Failed to update employment status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleOpenEditCostCenter = async () => {
    setSelectedCostCenterIds(selectedEmployee?.financial?.costCenterIds ? selectedEmployee.financial.costCenterIds.map(Number) : (selectedEmployee?.financial?.costCenterId ? [Number(selectedEmployee.financial.costCenterId)] : []));
    setEditCostCenterOpen(true);
    if (costCenters.length === 0) {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_URL}/api/cost-centers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          setCostCenters(res.data.data || []);
        }
      } catch (err) {
        //console.error("Error fetching cost centers:", err);
      }
    }
  };

  const handleUpdateCostCenter = async () => {
    try {
      setUpdatingCostCenter(true);
      const token = localStorage.getItem("token");
      
      const response = await axios.put(
        `${API_URL}/api/financial/${selectedEmployee.employee_id}`,
        {
          costCenterIds: selectedCostCenterIds
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        const ccObjects = costCenters.filter(cc => selectedCostCenterIds.includes(cc.id));
        const ccNameCode = ccObjects.length > 0
          ? ccObjects.map(cc => `${cc.name} (${cc.code})`).join(', ')
          : "Not Assigned";

        const updatedEmployees = employees.map(emp => {
          if (emp.employee_id === selectedEmployee.employee_id) {
            return {
              ...emp,
              financial: {
                ...emp.financial,
                costCenterId: selectedCostCenterIds.length > 0 ? selectedCostCenterIds[0] : null,
                costCenterIds: selectedCostCenterIds,
                costCenter: ccNameCode
              }
            };
          }
          return emp;
        });
        setEmployees(updatedEmployees);

        setSelectedEmployee({
          ...selectedEmployee,
          financial: {
            ...selectedEmployee.financial,
            costCenterId: selectedCostCenterIds.length > 0 ? selectedCostCenterIds[0] : null,
            costCenterIds: selectedCostCenterIds,
            costCenter: ccNameCode
          }
        });

        setEditCostCenterOpen(false);
      }
    } catch (error) {
      //console.error("Failed to update cost center:", error);
      alert(error.response?.data?.message || "Failed to update Cost Center");
    } finally {
      setUpdatingCostCenter(false);
    }
  };

  // Function to generate formatted filename
  const generateFileName = (employee, documentType, originalFileName) => {
    const firstName = employee.personal.firstName || '';
    const lastName = employee.personal.lastName || '';
    const employeeId = employee.employee_id || '';

    // Clean the names for filename
    const cleanFirstName = firstName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const cleanLastName = lastName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

    // Get file extension from original filename
    const fileExtension = originalFileName ? originalFileName.split('.').pop() : 'pdf';

    // Map document types to readable names
    const documentTypeMap = {
      'panCardFile': nationalIdLabel.replace(/\s+/g, '_'),
      'adharCardFile': 'Aadhar_Card',
      'qualificationFile': 'Qualification',
      'certificationFile': 'Certification',
      'personalPhoto': 'Profile_Photo'
    };

    const docType = documentTypeMap[documentType] || documentType;

    return `${cleanFirstName}_${cleanLastName}_${employeeId}_${docType}.${fileExtension}`;
  };

  // Function to download individual document
  const downloadDocument = async (employee, documentType, originalFileName) => {
    if (!originalFileName) {
      alert('No document available to download');
      return;
    }

    try {
      setDownloading(true);
      const documentUrl = originalFileName.startsWith('http') ? originalFileName : `${API_URL}/uploads/${originalFileName}`;

      const response = await fetch(documentUrl);
      const blob = await response.blob();

      // Generate formatted filename
      const fileName = generateFileName(employee, documentType, originalFileName);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      //console.error('Download error:', error);
      alert('Failed to download document');
    } finally {
      setDownloading(false);
    }
  };

  // Function to download all documents as a zip file
  const downloadAllDocuments = async (employee) => {
    const documents = [
      { type: 'panCardFile', file: employee.personal.panCardFile, name: nationalIdLabel },
      ...((!companyCountry || companyCountry.toLowerCase() === 'india') ? [{ type: 'adharCardFile', file: employee.personal.adharCardFile, name: 'Aadhar Card' }] : []),
      { type: 'qualificationFile', file: employee.personal.qualificationFile, name: 'Qualification' },
      { type: 'certificationFile', file: employee.personal.certificationFile, name: 'Certification' },
      { type: 'personalPhoto', file: employee.personal.personalPhoto, name: 'Profile Photo' }
    ].filter(doc => doc.file); // Only include documents that exist

    if (documents.length === 0) {
      alert('No documents available to download');
      return;
    }

    try {
      setDownloading(true);

      // For multiple files, we'll create a zip using JSZip
      const JSZip = await import('jszip');
      const zip = new JSZip.default();

      // Add each document to the zip
      for (const doc of documents) {
        try {
          const fileUrl = doc.file.startsWith('http') ? doc.file : `${API_URL}/uploads/${doc.file}`;
          const response = await fetch(fileUrl);
          const blob = await response.blob();
          const fileName = generateFileName(employee, doc.type, doc.file);
          zip.file(fileName, blob);
        } catch (error) {
          //console.error(`Error downloading ${doc.name}:`, error);
        }
      }

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Create download link for zip
      const firstName = employee.personal.firstName || '';
      const lastName = employee.personal.lastName || '';
      const employeeId = employee.employee_id || '';
      const cleanFirstName = firstName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const cleanLastName = lastName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

      const zipFileName = `${cleanFirstName}_${cleanLastName}_${employeeId}_all_documents.zip`;

      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      //console.error('Zip creation error:', error);
      alert('Failed to create documents package');
    } finally {
      setDownloading(false);
      setDownloadMenuAnchor(null);
    }
  };

  // Function to download all documents as a single PDF (simplified version)
  const downloadAllAsPDF = async (employee) => {
    // This is a simplified version that creates a PDF with document links
    // For actual PDF merging, you'd need a server-side solution
    try {
      setDownloading(true);

      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();

      // Add employee information
      doc.setFontSize(16);
      doc.text('Employee Documents Summary', 20, 20);
      doc.setFontSize(12);
      doc.text(`Employee: ${employee.personal.firstName} ${employee.personal.lastName}`, 20, 35);
      doc.text(`Employee ID: ${employee.employee_id}`, 20, 45);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 55);

      // List available documents
      let yPosition = 75;
      doc.text('Available Documents:', 20, yPosition);
      yPosition += 10;

      const documents = [
        { type: nationalIdLabel, file: employee.personal.panCardFile },
        ...((!companyCountry || companyCountry.toLowerCase() === 'india') ? [{ type: 'Aadhar Card', file: employee.personal.adharCardFile }] : []),
        { type: 'Qualification', file: employee.personal.qualificationFile },
        { type: 'Certification', file: employee.personal.certificationFile },
        { type: 'Profile Photo', file: employee.personal.personalPhoto }
      ].filter(docItem => docItem.file);

      documents.forEach((docItem, index) => {
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(`${index + 1}. ${docItem.type}: ${docItem.file}`, 25, yPosition);
        yPosition += 8;
      });

      // Add note about individual downloads
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.text('Note: Individual documents can be downloaded from the employee portal.', 20, yPosition + 10);

      // Generate filename
      const firstName = employee.personal.firstName || '';
      const lastName = employee.personal.lastName || '';
      const employeeId = employee.employee_id || '';
      const cleanFirstName = firstName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const cleanLastName = lastName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();

      const pdfFileName = `${cleanFirstName}_${cleanLastName}_${employeeId}_documents_summary.pdf`;

      // Save PDF
      doc.save(pdfFileName);

    } catch (error) {
      //console.error('PDF creation error:', error);
      alert('Failed to create PDF summary');
    } finally {
      setDownloading(false);
      setDownloadMenuAnchor(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    updateSearchParams({ page: (newPage + 1).toString() });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchParams({
      limit: newRowsPerPage.toString(),
      page: '1' // Reset to first page
    });
  };

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setDialogOpen(true);
    setDialogTab(0);
  };

  const handleViewDocument = (documentName, documentType) => {
    if (!documentName) return;

    const documentUrl = documentName.startsWith('http') ? documentName : `${API_URL}/uploads/${documentName}`;
    setSelectedDocument({
      name: documentName,
      url: documentUrl,
      type: documentType
    });
    setDocumentViewerOpen(true);
  };

  const handleSearch = () => {
    updateSearchParams({
      search: localSearchQuery,
      field: searchField,
      page: '1' // Reset to first page on search
    });
  };

  const handleClearSearch = () => {
    setLocalSearchQuery('');
    updateSearchParams({ search: '', field: 'all', page: '1' });
  };

  const handleSearchFieldChange = (event) => {
    const newField = event.target.value;
    setLocalSearchQuery(''); // Clear query when changing field
    updateSearchParams({
      field: newField,
      search: '',
      page: '1'
    });
  };

  const handleSearchQueryChange = (event) => {
    setLocalSearchQuery(event.target.value);
  };

  const getSearchPlaceholder = () => {
    switch (searchField) {
      case 'employee_id': return 'Search by Employee ID';
      case 'firstName': return 'Search by First Name';
      case 'lastName': return 'Search by Last Name';
      case 'department': return 'Search by Department';
      default: return 'Search all fields';
    }
  };

  // Client-side pagination for search results
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedEmployees = isSearching
    ? employees.slice(startIndex, endIndex)
    : employees;

  const TabPanel = ({ children, value, index, ...other }) => (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );

  const getDocumentIcon = (filename) => {
    if (filename?.toLowerCase().endsWith('.pdf')) return <PictureAsPdfIcon />;
    if (filename?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/)) return <ImageIcon />;
    return <DescriptionIcon />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Available';
    return new Date(dateString).toLocaleDateString(undefined);
  };

  if (loading && !employees.length) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Typography variant="h4" gutterBottom  fontWeight="bold">
        View Employee Details
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <IconButton color="inherit" size="small" onClick={handleRetry}>
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Search Field</InputLabel>
                <Select
                  value={searchField}
                  label="Search Field"
                  onChange={handleSearchFieldChange}
                >
                  <MenuItem value="all">All Fields</MenuItem>
                  <MenuItem value="employee_id">Employee ID</MenuItem>
                  <MenuItem value="firstName">First Name</MenuItem>
                  <MenuItem value="lastName">Last Name</MenuItem>

                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4} md={8}>
              <TextField
                label={getSearchPlaceholder()}
                variant="outlined"
                fullWidth
                value={localSearchQuery}
                onChange={handleSearchQueryChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: localSearchQuery && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch} edge="end" size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={2}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Employee ID</strong></TableCell>
              <TableCell><strong>Full Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Date of Birth</strong></TableCell>
              <TableCell><strong>Gender</strong></TableCell>
              {(!companyCountry || companyCountry.toLowerCase() === 'india') && (
                <TableCell><strong>Aadhar Card No</strong></TableCell>
              )}
              <TableCell><strong>Bank Name</strong></TableCell>
              <TableCell><strong>Account Number</strong></TableCell>
              <TableCell><strong>{nationalIdLabel}</strong></TableCell>
              <TableCell><strong>{bankCodeLabel}</strong></TableCell>
              <TableCell><strong>Tenure</strong></TableCell>  {/* NEW: Added Tenure column */}
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedEmployees.length > 0 ? (
              paginatedEmployees.map((employee) => (
                <TableRow key={employee.employee_id} hover>
                  <TableCell
                    style={{
                      color: "#1976d2",
                      cursor: "pointer",
                      textDecoration: "underline"
                    }}
                    onClick={() => handleViewDetails(employee)}
                  >
                    {employee.employee_id || "Not Available"}
                  </TableCell>
                  <TableCell>
                    {
                      `${employee.personal.firstName || ''} ${employee.personal.lastName || ''}`.trim() ||
                      "Not Available"}
                  </TableCell>
                  <TableCell>{employee.personal.personalemail || "Not Available"}</TableCell>
                  <TableCell>{formatDate(employee.personal.dateOfBirth)}</TableCell>
                  <TableCell>{employee.personal.gender || "Not Specified"}</TableCell>
                  {(!companyCountry || companyCountry.toLowerCase() === 'india') && (
                    <TableCell>{employee.personal.adharCardNumber || "Not Available"}</TableCell>
                  )}
                  <TableCell>{employee.financial.bankName || "Not Available"}</TableCell>
                  <TableCell>{employee.financial.accountNumber || "Not Available"}</TableCell>
                  <TableCell>{employee.personal.panNumber || "Not Available"}</TableCell>
                  <TableCell>{employee.financial.ifscCode || "Not Available"}</TableCell>
                  <TableCell>{employee.personal.tenure || "Not Available"}</TableCell>  {/* NEW: Added Tenure cell */}
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleViewDetails(employee)}
                      title="View Details"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={12} align="center">  {/* Updated colSpan to 12 */}
                  No employees found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page - 1} // MUI expects 0-based
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>


      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '85vh',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2.5}>
              {selectedEmployee?.personal?.personalPhoto && (
                <Avatar
                  src={selectedEmployee.personal.personalPhoto.startsWith('http') ? selectedEmployee.personal.personalPhoto : `${API_URL}/uploads/${selectedEmployee.personal.personalPhoto}`}
                  sx={{
                    width: 72,
                    height: 72,
                    border: '3px solid',
                    borderColor: 'primary.light',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                />
              )}
              <Box>
                <Typography variant="h5" fontWeight={600} color="text.primary">
                  {selectedEmployee?.roles?.fullName ||
                    `${selectedEmployee?.personal?.firstName || ''} ${selectedEmployee?.personal?.lastName || ''}`.trim()}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Employee ID: <strong>{selectedEmployee?.employee_id}</strong>
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={selectedEmployee?.personal?.employmentStatus || 'Active'}
                    color={selectedEmployee?.personal?.employmentStatus === 'active' ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                  <Tooltip title="Edit Employment Status">
                    <IconButton size="small" sx={{ ml: 1 }} onClick={() => {
                      setNewStatus(selectedEmployee?.personal?.employmentStatus || 'active');
                      setEditStatusOpen(true);
                    }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            <IconButton
              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
              disabled={downloading}
              sx={{
                bgcolor: 'action.hover',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              {downloading ? <CircularProgress size={24} /> : <MoreVertIcon />}
            </IconButton>

            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
              PaperProps={{
                sx: { minWidth: 200 }
              }}
            >
              <MenuItem
                onClick={() => downloadAllDocuments(selectedEmployee)}
                disabled={downloading}
              >
                <DownloadIcon sx={{ mr: 1.5 }} />
                Download All as ZIP
              </MenuItem>
            </Menu>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: 'grey.50' }}>
          <Tabs
            value={dialogTab}
            onChange={(e, newValue) => setDialogTab(newValue)}
            variant="fullWidth"
              
                scrollButtons="auto"
                allowScrollButtonsMobile
            sx={{
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.95rem'
              }
            }}
          >
            <Tab icon={<PersonIcon />} label="Personal" iconPosition="start" />
            <Tab icon={<WorkIcon />} label="Professional" iconPosition="start" />
            <Tab icon={<AccountBalanceIcon />} label="Financial" iconPosition="start" />
            <Tab icon={<ContactMailIcon />} label="Contact" iconPosition="start" />
            <Tab icon={<SchoolIcon />} label="Education" iconPosition="start" />
            <Tab icon={<CardMembershipIcon />} label="Documents" iconPosition="start" />
          </Tabs>

          <DialogContent sx={{ p: 3 }}>
            {/* Personal Info Tab */}
            <TabPanel value={dialogTab} index={0}>
              {selectedEmployee && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <PersonIcon color="primary" /> Basic Information
                        </Typography>
                        <List disablePadding>
                          {[
                            ['First Name', selectedEmployee.personal.firstName],
                            ['Last Name', selectedEmployee.personal.lastName],
                            ['Father\'s Name', selectedEmployee.personal.fatherName],
                            ['Date of Birth', formatDate(selectedEmployee.personal.dateOfBirth)],
                            ['Gender', selectedEmployee.personal.gender || 'Not Specified'],
                            ['Anniversary', formatDate(selectedEmployee.personal.anniversary)]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper', mb: 3 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <ContactEmergencyIcon color="primary" /> Identification
                        </Typography>
                        <List disablePadding>
                          {[
                            [`${nationalIdLabel}`, selectedEmployee.personal.panNumber],
                            ...((!companyCountry || companyCountry.toLowerCase() === 'india') ? [['Aadhar Number', selectedEmployee.personal.adharCardNumber]] : [])
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                          <HealthAndSafetyIcon color="primary" /> Insurance & Nominee
                        </Typography>
                        <List disablePadding>
                          {[
                            ['Individual Insurance', selectedEmployee.personal.individualInsurance],
                            ['Group Insurance', selectedEmployee.personal.groupInsurance],
                            ['Nominee Name', selectedEmployee.personal.nomineeName],
                            ['Relationship', selectedEmployee.personal.relationship],
                            ['Nominee Age', selectedEmployee.personal.nomineeAge]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </TabPanel>

            {/* Professional Tab */}
            <TabPanel value={dialogTab} index={1}>
              {selectedEmployee && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Role Information</Typography>
                        <List disablePadding>
                          {[
                            ['Designation', selectedEmployee.roles.designation],
                            ['Department', selectedEmployee.roles.department || selectedEmployee.financial.department],
                            ['Role Type', selectedEmployee.roles.roleType],
                            ['Joining Date', formatDate(selectedEmployee.roles.joiningDate)],
                            ['Grade', selectedEmployee.roles.grade],
                            ['Tenure', selectedEmployee.personal.tenure]  // NEW: Added tenure
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                          <ListItem sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={<Typography variant="caption" color="text.secondary">Cost Center</Typography>}
                              secondary={
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body1" fontWeight={500}>{selectedEmployee.financial.costCenter || 'Not Assigned'}</Typography>
                                    <Tooltip title="Edit Cost Center">
                                      <IconButton size="small" onClick={handleOpenEditCostCenter}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                   you can edit the cost centers assigned to this employee.
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Employment Details</Typography>
                        <List disablePadding>
                          <ListItem sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={<Typography variant="caption" color="text.secondary">Employment Status</Typography>}
                              secondary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body1" fontWeight={500}>{selectedEmployee.personal.employmentStatus || 'Not Available'}</Typography>
                                  <Tooltip title="Edit Employment Status">
                                    <IconButton size="small" onClick={() => {
                                      setNewStatus(selectedEmployee.personal.employmentStatus || 'active');
                                      setEditStatusOpen(true);
                                    }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              }
                            />
                          </ListItem>
                          {[
                            ['Resignation Date', formatDate(selectedEmployee.financial.resignationDate)],
                            ['Notice Period', selectedEmployee.financial.noticePeriod],
                            ['Credit Card Offered', selectedEmployee.financial.creditCardOffered]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                        {selectedEmployee.roles.selectedResponsibilities && selectedEmployee.roles.selectedResponsibilities.length > 0 && (
                          <>
                            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Responsibilities</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {selectedEmployee.roles.selectedResponsibilities.map((resp, index) => (
                                <Chip key={index} label={resp} size="small" color="primary" variant="outlined" />
                              ))}
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </TabPanel>

            {/* Financial Tab */}
            <TabPanel value={dialogTab} index={2}>
              {selectedEmployee && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Bank Details</Typography>
                        <List disablePadding>
                          {[
                            ['Bank Name', selectedEmployee.financial.bankName],
                            ['Account Holder Name', selectedEmployee.financial.accountHolderName],
                            ['Account Number', selectedEmployee.financial.accountNumber],
                            ['Account Type', selectedEmployee.financial.accountType],
                            ['Branch', selectedEmployee.financial.branch],
                            [`${bankCodeLabel}`, selectedEmployee.financial.ifscCode]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Salary Information</Typography>
                        <List disablePadding>
                          {[
                            ['Current Salary', selectedEmployee.financial.currentSalary ? `â‚¹${selectedEmployee.financial.currentSalary}` : null],
                            ['Previous Salary', selectedEmployee.financial.previousSalary ? `â‚¹${selectedEmployee.financial.previousSalary}` : null],
                            ['CTC', selectedEmployee.financial.ctc ? `â‚¹${selectedEmployee.financial.ctc}` : null],
                            ['Tax Calculation', selectedEmployee.financial.taxCalculation ? `â‚¹${selectedEmployee.financial.taxCalculation}` : null],
                            ['Advance Salary', selectedEmployee.financial.advanceSalary]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                          <ListItem sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={<Typography variant="caption" color="text.secondary">Cost Center</Typography>}
                              secondary={
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body1" fontWeight={500}>{selectedEmployee.financial.costCenter || 'Not Assigned'}</Typography>
                                    <Tooltip title="Edit Cost Center">
                                      <IconButton size="small" onClick={handleOpenEditCostCenter}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                   you can edit the cost centers assigned to this employee.
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </TabPanel>

            {/* Contact Tab */}
            <TabPanel value={dialogTab} index={3}>
              {selectedEmployee && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Contact Information</Typography>
                        <List disablePadding>
                          {[
                            ['Personal Email', selectedEmployee.personal.personalemail],
                            ['Company Email', selectedEmployee.personal.companyemail],
                            ['Phone Number', selectedEmployee.personal.phoneNumber],
                            ['Mobile', selectedEmployee.personal.mobile],
                            ['Landline', selectedEmployee.personal.landline]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Address</Typography>
                        <List disablePadding>
                          <ListItem sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={<Typography variant="caption" color="text.secondary">Complete Address</Typography>}
                              secondary={
                                <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
                                  {`
${selectedEmployee.personal.houseNumber || ''} ${selectedEmployee.personal.street || ''}
${selectedEmployee.personal.state || ''} ${selectedEmployee.personal.area || ''}
${selectedEmployee.personal.city || ''} ${selectedEmployee.personal.pinCode || ''}
                          `.trim() || 'Not Available'}
                                </Typography>
                              }
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={<Typography variant="caption" color="text.secondary">City</Typography>}
                              secondary={<Typography variant="body1" fontWeight={500}>{selectedEmployee.personal.city || 'Not Available'}</Typography>}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={<Typography variant="caption" color="text.secondary">Pin Code</Typography>}
                              secondary={<Typography variant="body1" fontWeight={500}>{selectedEmployee.personal.pinCode || 'Not Available'}</Typography>}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </TabPanel>

            {/* Education Tab */}
            <TabPanel value={dialogTab} index={4}>
              {selectedEmployee && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Education</Typography>
                        <List disablePadding>
                          {[
                            ['Degree', selectedEmployee.personal.degree],
                            ['Institution', selectedEmployee.personal.institution],
                            ['Year', selectedEmployee.personal.year]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Certifications</Typography>
                        <List disablePadding>
                          {[
                            ['Certification Name', selectedEmployee.personal.certificationName],
                            ['Issued By', selectedEmployee.personal.issuedBy],
                            ['Certification Date', formatDate(selectedEmployee.personal.certificationDate)]
                          ].map(([label, value]) => (
                            <ListItem key={label} sx={{ py: 0.8 }}>
                              <ListItemText
                                primary={<Typography variant="caption" color="text.secondary">{label}</Typography>}
                                secondary={<Typography variant="body1" fontWeight={500}>{value || 'Not Available'}</Typography>}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel value={dialogTab} index={5}>
              {selectedEmployee && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Documents</Typography>
                    <List>
                      {[
                        [`${nationalIdLabel}`, selectedEmployee.personal.panCardFile, 'panCardFile'],
                        ...((!companyCountry || companyCountry.toLowerCase() === 'india') ? [['Aadhar Card', selectedEmployee.personal.adharCardFile, 'adharCardFile']] : []),
                        ['Qualification', selectedEmployee.personal.qualificationFile, 'qualificationFile'],
                        ['Certification', selectedEmployee.personal.certificationFile, 'certificationFile']
                      ].map(([label, file, key]) => (
                        <ListItem key={key} secondaryAction={
                          <>
                            <IconButton
                              onClick={() => handleViewDocument(file, label)}
                              disabled={!file}
                              title="View Document"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => downloadDocument(selectedEmployee, key, file)}
                              disabled={!file || downloading}
                              title="Download Document"
                            >
                              <DownloadIcon />
                            </IconButton>
                          </>
                        }>
                          <ListItemIcon>
                            {getDocumentIcon(file)}
                          </ListItemIcon>
                          <ListItemText
                            primary={label}
                            secondary={file ? cleanFileName(file) : "Not Uploaded"}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Profile Photo</Typography>
                    <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" gap={2}>
                      {selectedEmployee.personal.personalPhoto ? (
                        <>
                          <Avatar
                            src={selectedEmployee.personal.personalPhoto.startsWith('http') ? selectedEmployee.personal.personalPhoto : `${API_URL}/uploads/${selectedEmployee.personal.personalPhoto}`}
                            sx={{ width: 200, height: 200 }}
                            variant="rounded"
                          />
                          <Box display="flex" gap={1}>
                            <Button
                              variant="outlined"
                              onClick={() => handleViewDocument(selectedEmployee.personal.personalPhoto, 'Profile Photo')}
                              startIcon={<ImageIcon />}
                            >
                              View Full Size
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => downloadDocument(selectedEmployee, 'personalPhoto', selectedEmployee.personal.personalPhoto)}
                              disabled={downloading}
                              startIcon={<DownloadIcon />}
                            >
                              Download
                            </Button>
                          </Box>
                        </>
                      ) : (
                        <Typography color="text.secondary">No Profile Photo Uploaded</Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              )}
            </TabPanel>
          </DialogContent>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer Dialog */}
      <Dialog
        open={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {selectedDocument?.type} - {cleanFileName(selectedDocument?.name)}
        </DialogTitle>
        <DialogContent dividers>
          {selectedDocument && (
            <Box sx={{ height: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {selectedDocument.name.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={selectedDocument.url}
                  width="100%"
                  height="100%"
                  title={selectedDocument.name}
                  style={{ border: 'none' }}
                />
              ) : selectedDocument.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
                <img
                  src={selectedDocument.url}
                  alt={selectedDocument.name}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <Box textAlign="center">
                  <DescriptionIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                    Document cannot be previewed
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => selectedEmployee && downloadDocument(
                      selectedEmployee,
                      selectedDocument.type.toLowerCase().replace(' ', '') + 'File',
                      selectedDocument.name
                    )}
                    sx={{ mt: 2 }}
                    disabled={downloading}
                  >
                    {downloading ? <CircularProgress size={24} /> : 'Download Document'}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedDocument && selectedEmployee && (
            <Button
              onClick={() => downloadDocument(
                selectedEmployee,
                selectedDocument.type.toLowerCase().replace(' ', '') + 'File',
                selectedDocument.name
              )}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            >
              Download
            </Button>
          )}
          <Button onClick={() => setDocumentViewerOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Employment Status Dialog */}
      <Dialog open={editStatusOpen} onClose={() => !updatingStatus && setEditStatusOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Employment Status</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Employment Status</InputLabel>
            <Select
              value={newStatus}
              label="Employment Status"
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={updatingStatus}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="deceased">Deceased</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStatusOpen(false)} color="inherit" disabled={updatingStatus}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            color="primary"
            variant="contained"
            disabled={updatingStatus || newStatus === selectedEmployee?.personal?.employmentStatus}
          >
            {updatingStatus ? <CircularProgress size={24} color="inherit" /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Cost Center Dialog */}
      <Dialog open={editCostCenterOpen} onClose={() => !updatingCostCenter && setEditCostCenterOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Cost Centers</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="cost-centers-select-label">Cost Centers</InputLabel>
            <Select
              labelId="cost-centers-select-label"
              multiple
              value={selectedCostCenterIds}
              label="Cost Centers"
              onChange={(e) => setSelectedCostCenterIds(e.target.value)}
              disabled={updatingCostCenter}
              renderValue={(selected) => 
                selected.map(val => {
                  const cc = costCenters.find(c => c.id === Number(val));
                  return cc ? `${cc.name} (${cc.code})` : val;
                }).join(', ')
              }
            >
              {costCenters.map((cc) => (
                <MenuItem key={cc.id} value={cc.id}>
                  <Checkbox checked={selectedCostCenterIds.indexOf(cc.id) > -1} />
                  <ListItemText primary={`${cc.name} (${cc.code})`} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText sx={{ color: 'text.secondary', mt: 1 }}>
              As a manager, you can edit and select multiple cost centers.
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCostCenterOpen(false)} color="inherit" disabled={updatingCostCenter}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateCostCenter}
            color="primary"
            variant="contained"
            disabled={updatingCostCenter || JSON.stringify(selectedCostCenterIds) === JSON.stringify(selectedEmployee?.financial?.costCenterIds || [])}
          >
            {updatingCostCenter ? <CircularProgress size={24} color="inherit" /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewEmployees;